<template>
  <section>
    <header class="card-hdr">
      <div class="card-title">
        <h3>{{ title }}</h3>
        <div class="current-stats" v-if="currentTemp !== undefined || currentHum !== undefined">
          <!-- Temp Stats -->
          <span v-if="(type === 'temp' || type === 'both') && currentTemp !== undefined" class="stat-item temp">
            {{ formatTemp(currentTemp) }}
          </span>

          <!-- Hum Stats -->
          <span v-if="(type === 'hum' || type === 'both') && currentHum !== undefined" class="stat-item hum">
            {{ formatHum(currentHum) }}
          </span>

          <span v-if="lastUpdateAgo" class="stat-last-update">
            {{ lastUpdateAgo }}
          </span>
        </div>
      </div>
      <!-- Header actions -->
      <div class="card-tools">
        <slot name="header-actions" />
        <div class="divider"></div>
      <button
        v-for="r in ['24h', '7d', '30d']"
        :key="r"
        class="time-btn"
        :class="{ active: currentRange === r }"
        @click="$emit('update:range', r)"
      >
        {{ r }}
      </button>
      
      <!-- Zoom Controls -->
      <button 
        class="time-btn" 
        title="Zoom Out"
        @click="zoomOut"
      >
        −
      </button>
      <button 
        class="time-btn" 
        title="Zoom In"
        @click="zoomIn"
      >
        +
      </button>
      <button 
        class="time-btn" 
        title="Reset Zoom"
        @click="resetZoom"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
        </svg>
      </button>

      <button 
          class="time-btn"
          title="Threshold Settings"
          @click="$emit('config')"
        >
          <FontAwesomeIcon :icon="faSliders" />
        </button>

        <!-- Limits Display in Toolbar -->
        <div class="toolbar-limits">
          <span v-if="(type === 'temp' || type === 'both') && (tempMin !== undefined || tempMax !== undefined)" class="limit-group">
            <span v-if="tempMin !== undefined" class="temp-limit-val min" title="Min Temp Threshold" @click="$emit('config')">MIN: {{ tempMin }}</span>
            <span v-if="tempMax !== undefined" class="temp-limit-val max" title="Max Temp Threshold" @click="$emit('config')">MAX: {{ tempMax }}</span>
          </span>
          <span v-if="(type === 'hum' || type === 'both') && (humMin !== undefined || humMax !== undefined)" class="limit-group">
            <span v-if="humMin !== undefined" class="hum-limit-val min" title="Min Hum Threshold" @click="$emit('config')">MIN: {{ humMin }}</span>
            <span v-if="humMax !== undefined" class="hum-limit-val max" title="Max Hum Threshold" @click="$emit('config')">MAX: {{ humMax }}</span>
          </span>
        </div>
    </div>
  </header>

    <div class="card-body chart-body chart-container-rel" @wheel="handleWheel">
      <div v-if="loading" class="loading-overlay">
        <span class="blinking-text">Loading...</span>
      </div>
      <Line ref="chartRef" :data="chartData" :options="chartOptions" />
      <div class="zoom-hint-overlay" :class="{ show: showZoomHint }">
        <div class="zoom-hint-content">
          Usa <kbd>Alt</kbd> + rotella per zoomare
        </div>
      </div>
    </div>
</section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  Filler,
} from "chart.js";
import { Line } from "vue-chartjs";
import "chartjs-adapter-luxon";
import zoomPlugin from "chartjs-plugin-zoom";
import { chartTimeConfig } from "@/utils/chartTime";

// Registra i componenti necessari
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  Filler,
  zoomPlugin
);

const props = defineProps<{
  title: string;
  type: "temp" | "hum" | "both";
  seriesT: { x: number; y: number }[];
  seriesH: { x: number; y: number }[];
  currentRange: string;
  currentTemp?: number;
  currentHum?: number;
  lastUpdateAgo?: string;
  tempMin?: number;
  tempMax?: number;
  humMin?: number;
  humMax?: number;
  loading?: boolean;
}>();

import { faSliders } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

/* Formatting Helpers */
const formatTemp = (v: number) => `${v.toFixed(1)}°C`;
const formatHum = (v: number) => `${Math.round(v)}%`;

defineEmits(["update:range", "refresh", "config"]);

const chartRef = ref<any>(null);
const showZoomHint = ref(false);
let hintTimeout: any = null;

function handleWheel(e: WheelEvent) {
  if (!e.altKey) {
    showZoomHint.value = true;
    if (hintTimeout) clearTimeout(hintTimeout);
    hintTimeout = setTimeout(() => {
      showZoomHint.value = false;
    }, 2000);
  } else {
    showZoomHint.value = false;
  }
}

function resetZoom() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.resetZoom();
  }
}

function zoomIn() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.zoom(1.2);
  }
}

function zoomOut() {
  if (chartRef.value?.chart) {
    chartRef.value.chart.zoom(0.8);
  }
}

// Watch per il cambio range nel componente TH (Temperature/Humidity)
watch(() => props.currentRange, (newRange: string) => {
  if (chartRef.value?.chart) {
    updateChartIntervals(chartRef.value.chart, newRange);
  }
});

function updateChartIntervals(chart: any, forcedRange: string | null = null) {
  if (!chart) return;
  
  let rangeHours = 24; // Default fallback

  if (forcedRange) {
    if (forcedRange === "1h") rangeHours = 1;
    else if (forcedRange === "24h") rangeHours = 24;
    else if (forcedRange === "7d") rangeHours = 168;
    else if (forcedRange === "30d") rangeHours = 720;
  } else {
    const scaleX = chart.scales.x;
    if (scaleX && scaleX.min !== undefined && scaleX.max !== undefined) {
      const rangeMs = scaleX.max - scaleX.min;
      rangeHours = rangeMs / (1000 * 60 * 60);
    }
  }

  let unit = "minute" as const;
  let stepSize = 30;

  if (rangeHours <= 1.1) {
    stepSize = 1;
  } else if (rangeHours <= 4.1) {
    stepSize = 10;
  } else if (rangeHours <= 8.1) {
    stepSize = 15;
  } else {
    stepSize = 30;
  }

  if (chart.options.scales.x.time && chart.options.scales.x.ticks) {
    chart.options.scales.x.time.unit = unit;
    chart.options.scales.x.time.stepSize = stepSize;
    chart.options.scales.x.bounds = 'ticks';
    if (rangeHours <= 25.1) {
      chart.options.scales.x.ticks.autoSkip = false;
      chart.options.scales.x.ticks.maxRotation = 45;
      chart.options.scales.x.ticks.minRotation = 45;
      chart.options.scales.x.ticks.maxTicksLimit = 100;
    } else {
      chart.options.scales.x.ticks.autoSkip = true;
      chart.options.scales.x.ticks.maxRotation = 0;
      chart.options.scales.x.ticks.minRotation = 0;
    }
  }
  chart.update("none");
}

const chartData = computed(() => {
  const datasets: any[] = [];
  
  if (props.type === 'temp' || props.type === 'both') {
    datasets.push({
      label: "Temperature (°C)",
      data: props.seriesT,
      borderColor: "#22c55e",
      backgroundColor: "rgba(34, 197, 94, 0.1)",
      yAxisID: "y",
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 2,
    });
  }

  if (props.type === 'hum' || props.type === 'both') {
    datasets.push({
      label: "Humidity (%)",
      data: props.seriesH,
      borderColor: "#8aeaff",
      backgroundColor: "rgba(138, 234, 255, 0.1)",
      yAxisID: props.type === 'both' ? "y1" : "y",
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 2,
    });
  }

  // Helper to get time range for straight lines
  const getTimeRange = () => {
    const dataSources = [];
    if (props.type === 'temp' || props.type === 'both') dataSources.push(...props.seriesT);
    if (props.type === 'hum' || props.type === 'both') dataSources.push(...props.seriesH);
    
    if (dataSources.length === 0) return [];
    
    // Combine all points to find min/max X (time)
    const allX = dataSources.map(p => p.x);
    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    
    return [minX, maxX];
  };

  const timeRange = getTimeRange();
  const hasData = timeRange.length === 2;

  if (hasData) {
    // Temperature Thresholds
    if (props.type === 'temp' || props.type === 'both') {
        if (props.tempMin != null) {
          datasets.push({
            label: "Temp Min Limit",
            data: [{ x: timeRange[0], y: props.tempMin }, { x: timeRange[1], y: props.tempMin }],
            borderColor: "rgba(34, 197, 94, 0.4)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: "y"
          });
        }
        if (props.tempMax != null) {
          datasets.push({
            label: "Temp Max Limit",
            data: [{ x: timeRange[0], y: props.tempMax }, { x: timeRange[1], y: props.tempMax }],
            borderColor: "rgba(239, 68, 68, 0.5)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: "y"
          });
        }
    }

    // Humidity Thresholds
    if (props.type === 'hum' || props.type === 'both') {
        const targetY = props.type === 'both' ? "y1" : "y";
        if (props.humMin != null) {
          datasets.push({
            label: "Hum Min Limit",
            data: [{ x: timeRange[0], y: props.humMin }, { x: timeRange[1], y: props.humMin }],
            borderColor: "rgba(138, 234, 255, 0.4)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: targetY
          });
        }
        if (props.humMax != null) {
          datasets.push({
            label: "Hum Max Limit",
            data: [{ x: timeRange[0], y: props.humMax }, { x: timeRange[1], y: props.humMax }],
            borderColor: "rgba(59, 130, 246, 0.5)",
            borderDash: [5, 5],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            yAxisID: targetY
          });
        }
    }
  }

  return { datasets };
});

const intervalConfig = computed(() => {
  let unit = "minute" as const;
  let stepSize = 30;

  if (props.currentRange === "1h") {
    stepSize = 1;
  } else {
    stepSize = 30;
  }

  return { unit, stepSize };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  scales: {
    x: {
      ...(chartTimeConfig.scales.x as any),
      bounds: 'ticks',
      time: {
        ...chartTimeConfig.scales.x.time,
        unit: intervalConfig.value.unit,
        stepSize: intervalConfig.value.stepSize,
        round: 'minute'
      },
      ticks: { 
        ...chartTimeConfig.scales.x.ticks,
        autoSkip: props.currentRange === '1h' || props.currentRange === '24h' ? false : true,
        maxRotation: 45,
        minRotation: 45,
        maxTicksLimit: 100,
        callback: function(value: any, index: any, ticks: any) {
          const date = new Date(value);
          if (props.currentRange !== '1h' && date.getUTCMinutes() % 30 !== 0) return null;
          return chartTimeConfig.scales.x.ticks.callback(value, index, ticks);
        }
      },
      adapters: { ...chartTimeConfig.scales.x.adapters },
    },
    y: {
      type: "linear" as const,
      display: true,
      position: "left" as const,
      grid: {
        color: "rgba(255, 255, 255, 0.05)",
      },
      ticks: { 
        color: props.type === 'hum' ? '#8aeaff' : "#9fb0c2" 
      },
    },
    y1: {
      type: "linear" as const,
      display: props.type === 'both',
      position: "right" as const,
      grid: {
        drawOnChartArea: false,
      },
      ticks: { color: "#8aeaff" },
    },
  },
  plugins: {
    legend: {
      labels: { color: "#c9d3de" },
    },
    tooltip: chartTimeConfig.plugins.tooltip,
    zoom: {
      zoom: {
        wheel: { 
          enabled: true,
          modifierKey: 'alt' as const
        },
        pinch: { enabled: true },
        mode: "x" as const,
        onZoom: ({ chart }: { chart: any }) => updateChartIntervals(chart),
      },
      pan: {
        enabled: true,
        mode: "x" as const,
        onPan: ({ chart }: { chart: any }) => updateChartIntervals(chart),
      },
    },
  },
}));
</script>

<style scoped>
.time-btn {
  background: transparent;
  border: 1px solid #2a2f34;
  color: #9fb0c2;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  display: inline-flex; /* align icon */
  align-items: center;
  justify-content: center;
}
.time-btn:hover {
  background: #1c2229;
  color: #fff;
}
.time-btn.active {
  background: #22c55e;
  border-color: #22c55e;
  color: #0b0e12;
  font-weight: 600;
}
.chart-body {
  height: 300px;
  position: relative;
}

/* Integrated Stats */
.current-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}
.stat-last-update {
  font-size: 0.7rem;
  color: #6a7480;
  font-weight: 400;
  margin-left: auto; /* Push to right if space allowed */
}

.stat-item {
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.03);
  padding: 2px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.stat-item.temp {
  color: #22c55e;
}
.stat-item.temp::before {
  content: "";
  display: block;
  width: 7px;
  height: 7px;
  background: #22c55e;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(34, 197, 94, 0.4);
}
.stat-item.hum {
  color: #8aeaff;
}
.stat-item.hum::before {
  content: "";
  display: block;
  width: 7px;
  height: 7px;
  background: #8aeaff;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(138, 234, 255, 0.4);
}

@media (max-width: 600px) {
  .current-stats {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .card-hdr {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    height: auto;
    padding-bottom: 12px;
  }

  .card-tools {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
    /* Move items to start to avoid big gaps */
    justify-content: flex-start;
  }

  /* Force the divider to huge margin or hide it if wrapping */
  .divider {
    display: none; 
  }

  /* Limits container on its own line if needed, or wrap nicely */
  .toolbar-limits {
    margin: 4px 0;
    width: 100%;
    order: 10; /* Move to bottom of tools if wrapped */
  }

  .time-btn {
    flex: 1 0 auto; /* Make buttons touch-friendly */
    justify-content: center;
    padding: 6px 10px;
  }
}


.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(11, 14, 18, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
  border-radius: 0 0 12px 12px;
}

.blinking-text {
  color: #22c55e;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  animation: blink 1.5s infinite ease-in-out;
  text-transform: uppercase;
}

@keyframes blink {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}

.divider {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 4px;
}

.toolbar-limits {
  display: flex;
  align-items: center;
  margin: 0 8px;
}

.limit-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.temp-limit-val,
.hum-limit-val {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9fb0c2;
  background: rgba(255, 255, 255, 0.03);
  padding: 3px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  cursor: pointer;
}

.temp-limit-val.min {
  border-color: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}
.temp-limit-val.max {
  border-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.hum-limit-val.min {
  border-color: rgba(138, 234, 255, 0.2);
  color: #8aeaff;
}
.hum-limit-val.max {
  border-color: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}
</style>
